import _ from 'underscore'
import Poll from './poll'
import Results from './results'
import ClickableImageRegion from './clickable_image_region'
import ClickableImageRegions from './clickable_image_regions'
import { CLICKABLE_REGION_REGEX } from '../util.js'

export default class ClickableImage extends Poll {
  get defaultType () {
    return 'clickable_image'
  }

  /**
   * Collection of participant submitted click results.
   * @return {Results}
   */
  get results () {
    if (!this._results) {
      this._results = new Results(this.attributes.results, { poll: this })
    }

    return this._results
  }

  initialize (options = {}) {
    super.initialize(...arguments)

    // Needed for compatibility with old backbone stuff in Rails. See
    // `app/assets/javascripts/lib/backbone/models/poll.js.coffee` in the rails
    // app for details.
    // TODO: Remove this once the old backbone stuff in Rails has been removed.
    this.model_type = this.type()

    this.regions = new ClickableImageRegions(this.attributes.options,
      { silent: true })
    this.regions.poll = this

    this.on('change:options', this.updateRegions)

    if (!this.hasOtherRegion()) {
      // Ghost region that handles clicks on coordinates that are not defined by
      // a region
      this.regions.add(new ClickableImageRegion({ value: ClickableImageRegion.CLICKABLE_REGION_OTHER }))
    }
  }

  otherRegion () {
    return this.regions.find(region => region.isOtherRegion())
  }

  hasOtherRegion () {
    return !_.isUndefined(this.otherRegion())
  }

  findRegion (relativeX, relativeY, imageWidth, imageHeight) {
    const r = this.regions.find(region => region.containsRelativeXY(relativeX, relativeY, imageWidth, imageHeight))

    return r || this.otherRegion()
  }

  highestResponse () {
    return this.regions && this.regions.length
      ? this.regions.highestResult()
      : null
  }

  isGraded () {
    return this.regions.hasCorrect()
  }

  updateRegions () {
    this.regions.set(this.attributes.options)
  }

  /**
   * Generate a rootless JSON object of all model attrs.
   * @param {Object} options
   * @return {Object}
   */
  toJSON (options = {}) {
    // Grab the generated JSON from the super class
    const json = super.toJSON(...arguments)

    // Append the options
    json.options = this.regions.invoke('toJSON')

    return json
  }

  parse (json = {}, options = {}) {
    json = super.parse(...arguments)

    _.defaults(options, { resetOptions: true })

    if (json.options && this.regions) {
      /*
       * Needs to be used while rails strips region names from the value field of
       * API calls. Check for the `remove_cip_region_name` option being removed
       * from rails ClickableImageSerializer in order to remove this
       */
      json.options.forEach((option) => {
        if (option.humanized_value &&
          CLICKABLE_REGION_REGEX.test(option.humanized_value)) {
          if (option.value !== option.humanized_value) {
            option.value = option.humanized_value
          }
        }
      })

      if (options.resetOptions) {
        this.regions.set(json.options, options)
      } else {
        delete json.options
      }
    }

    // All results are now included from the instance API and firehose channel.
    // When clearing results, remove all results and trigger `remove` events so
    // that views can remove individual results accordingly.
    const results = json.results_count === 0 ? [] : json.results
    this.results.set(results)
    delete json.results

    return json
  }

  /**
   * Parse Firehose response into the model.
   * @param {object} data Firehose response data.
   * @return {object} Returns potentially mutated data.
   */
  processLiveResponse (data = {}) {
    if (data.result) {
      if (data.result.deleted_at) {
        this.results.remove(data.result)
      } else {
        this.results.add(data.result, { merge: true })
      }

      // Result object is only contained within Firehose messages. Remove this
      // from the dataset before passing it to the model parser.
      delete data.result
    }

    // Process and parse data into the model. This must happen after any
    // mutations to the data response object.
    data = super.processLiveResponse(data)
    this.flashOptions().processLiveResponse(data)

    return data
  }

  sendToListUrl () {
    return undefined
  }
}
